// src/pages/health/WorkoutDayDetail.js
import { useMemo, useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO, isValid } from "date-fns";
import AuthCard from "../../components/auth/AuthCard";
import BottomNav from "../../components/common/BottomNav";
import { useLoadingGate } from "../../hooks/useLoadingGate";
import {
    ChevronDownIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/react/24/solid";

// ===== 유산소 이미지 import =====
import treadmillIcon from "../../assets/images/treadmill.png";
import cycle from "../../assets/images/cycle.png";
import stepper from "../../assets/images/stepper.png";

// ===== 웨이트 머신 15종 이미지 import =====
import legextension from "../../assets/images/legextension.png";
import seatedlegpress from "../../assets/images/seatedlegpress.png";
import seatedlegcurl from "../../assets/images/seatedlegcurl.png";
import seatedchestpress from "../../assets/images/seatedchestpress .png"; // 파일명 공백 주의
import butterfly from "../../assets/images/butterfly.png";
import inclinepress from "../../assets/images/inclinepress.png";
import latpulldown from "../../assets/images/latpulldown.png";
import longpull from "../../assets/images/longpull .png"; // 파일명 공백 주의
import reardeltfly from "../../assets/images/reardeltfly.png";
import shoulderpress from "../../assets/images/shoulderpress.png";
import armcurl from "../../assets/images/armcurl.png";
import dipping from "../../assets/images/dipping.png";
import abdominal from "../../assets/images/abdominal.png";
import rotarytorso from "../../assets/images/rotarytorso.png";
import inneroutthigh from "../../assets/images/inneroutthigh.png";

import MachineSettingModal from "./components/MachineSettingModal";

import {
    getMachineSettings,
    getCardioSettings,
    mergeSettingsIntoWorkouts,
} from "../../api/workoutSetting";

const fmt = {
    km: (v) => (v == null ? null : `${Number(v).toFixed(2)}km`),
    min: (v) => (v == null ? null : `${v}분`),
    percent: (v) => (v == null ? null : `${v}%`),
    kph: (v) => (v == null ? null : `${v}km/h`),
    kcal: (v) => (v == null ? null : `${v}kcal`),
    kg: (v) => (v == null ? null : `${v}kg`),
};

// 웨이트 15
const MACHINE_TITLES = [
    "레그익스텐션",
    "시티드 레그프레스",
    "시티드 레그컬",
    "시티드 체스트프레스",
    "버터플라이",
    "인클라인 프레스",
    "랫풀다운",
    "롱풀",
    "리어 델트플라이",
    "숄더프레스",
    "암컬",
    "디핑",
    "업도미널",
    "로타리 토르소",
    "이너/아웃싸이",
];

const MACHINE_SET = new Set(
    MACHINE_TITLES.map((t) => String(t).toLowerCase().replace(/\s/g, ""))
);

// 유산소 3
const CARDIO_TITLES = ["러닝머신", "자전거", "스텝머신"];

// 초기 렌더에 18개가 항상 보이도록 구성
const DUMMY_WORKOUTS = [
    ...MACHINE_TITLES.map((title, i) => ({
        id: i + 1,
        title,
        category: "웨이트",
    })),
    ...CARDIO_TITLES.map((title, i) => ({
        id: 100 + i,
        title,
        category: "유산소운동",
    })),
];

/* ---------------- 제목 → 유형 추론 ---------------- */
function normalizeTitle(title = "") {
    const t = String(title).toLowerCase().replace(/\s/g, "");
    if (t.includes("러닝머신") || t.includes("트레드밀") || t.includes("treadmill")) return "트레드밀";
    if (t.includes("자전거") || t.includes("사이클") || t.includes("cycle") || t.includes("bike")) return "사이클";
    if (t.includes("스텝머신") || t.includes("스텝퍼") || t.includes("stepper") || t.includes("계단")) return "스텝퍼";
    if (MACHINE_SET.has(t)) return t; // 일치하는 웨이트 머신 이름 반환
    return "기타";
}

// 중복 사용되는 웨이트 필드 통합
const WEIGHT_FIELDS = [
    ["무게", (w) => fmt.kg(w.weight_kg ?? w.sets?.[0]?.weight_kg)],
    [
        "세트",
        (w) =>
            w.sets_count != null
                ? `${w.sets_count}세트`
                : w.sets?.length != null
                    ? `${w.sets.length}세트`
                    : null,
    ],
    [
        "횟수",
        (w) =>
            w.reps != null
                ? `${w.reps}회`
                : w.sets?.[0]?.reps != null
                    ? `${w.sets[0].reps}회`
                    : null,
    ],
];

const EXERCISE_META = {
    // 유산소
    트레드밀: {
        icon: treadmillIcon,
        fields: [
            ["운동속도", (w) => fmt.kph(w.speed_kph ?? w.cardio?.speed_kph)],
            ["시간", (w) => fmt.min(w.duration_min)],
            ["거리", (w) => fmt.km(w.distance_km ?? w.cardio?.distance_km)],
            [
                "목표 심박수",
                (w) =>
                    w.target_hr_bpm != null
                        ? `${w.target_hr_bpm}bpm`
                        : (w.cardio?.target_hr_bpm != null
                            ? `${w.cardio.target_hr_bpm}bpm`
                            : null),
            ],
        ],
    },
    사이클: {
        icon: cycle,
        fields: [
            [
                "watt",
                (w) => {
                    const val = w.watt_w ?? w.cardio?.watt_w ?? w.cardio?.watt ?? w.watt;
                    return val != null ? `${val}W` : null;
                },
            ],
            ["시간", (w) => fmt.min(w.duration_min)],
            [
                "목표 심박수",
                (w) => {
                    const val = w.target_hr_bpm ?? w.cardio?.target_hr_bpm;
                    return val != null ? `${val}bpm` : null;
                },
            ],
        ],
    },
    스텝퍼: {
        icon: stepper,
        fields: [
            [
                "watt",
                (w) => {
                    const val = w.watt_w ?? w.cardio?.watt_w ?? w.cardio?.watt ?? w.watt;
                    return val != null ? `${val}W` : null;
                },
            ],
            ["시간", (w) => fmt.min(w.duration_min)],
            [
                "목표 심박수",
                (w) => {
                    const val = w.target_hr_bpm ?? w.cardio?.target_hr_bpm;
                    return val != null ? `${val}bpm` : null;
                },
            ],
        ],
    },

    // 웨이트 15종
    레그익스텐션: { icon: legextension, fields: WEIGHT_FIELDS },
    시티드레그프레스: { icon: seatedlegpress, fields: WEIGHT_FIELDS },
    시티드레그컬: { icon: seatedlegcurl, fields: WEIGHT_FIELDS },
    시티드체스트프레스: { icon: seatedchestpress, fields: WEIGHT_FIELDS },
    버터플라이: { icon: butterfly, fields: WEIGHT_FIELDS },
    인클라인프레스: { icon: inclinepress, fields: WEIGHT_FIELDS },
    랫풀다운: { icon: latpulldown, fields: WEIGHT_FIELDS },
    롱풀: { icon: longpull, fields: WEIGHT_FIELDS },
    리어델트플라이: { icon: reardeltfly, fields: WEIGHT_FIELDS },
    숄더프레스: { icon: shoulderpress, fields: WEIGHT_FIELDS },
    암컬: { icon: armcurl, fields: WEIGHT_FIELDS },
    디핑: { icon: dipping, fields: WEIGHT_FIELDS },
    업도미널: { icon: abdominal, fields: WEIGHT_FIELDS },
    로타리토르소: { icon: rotarytorso, fields: WEIGHT_FIELDS },
    "이너/아웃싸이": { icon: inneroutthigh, fields: WEIGHT_FIELDS },

    // 기타 (기본값)
    기타: { icon: legextension, fields: WEIGHT_FIELDS },
};

function pickMeta(w) {
    const key = normalizeTitle(w?.title);
    return EXERCISE_META[key] ?? EXERCISE_META["기타"];
}

/* ---------------- 라인 구성 (UI 그대로) ---------------- */
function metricLines(w) {
    const meta = pickMeta(w);
    const base = meta.fields.map(([label, getter]) => {
        const val = getter(w);
        return [label, val ?? "-"];
    });

    if (w.notes) base.push(["메모", w.notes]);
    return base;
}

/* ---------------- 컴포넌트 ---------------- */
export default function MachineConfig() {
    const ready = useLoadingGate();
    const [sp] = useSearchParams();

    const [isCatOpen, setIsCatOpen] = useState(false);
    const catRef = useRef(null);
    useEffect(() => {
        const onDocClick = (e) => {
            if (catRef.current && !catRef.current.contains(e.target)) setIsCatOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const dateStr = sp.get("date") || format(new Date(), "yyyy-MM-dd");
    const dateObj = useMemo(() => {
        const d = parseISO(dateStr);
        return isValid(d) ? d : new Date();
    }, [dateStr]);

    const [workouts, setWorkouts] = useState(DUMMY_WORKOUTS);
    const isLoading = false;

    useEffect(() => {
        (async () => {
            try {
                const [machines, cardios] = await Promise.all([
                    getMachineSettings(),
                    getCardioSettings(),
                ]);
                setWorkouts((prev) => mergeSettingsIntoWorkouts(prev, machines, cardios));
            } catch (e) {
                console.error("세팅 로딩 실패:", e);
            }
        })();
    }, []);

    const [cat, setCat] = useState("전체");
    const categories = useMemo(() => {
        const s = new Set(["전체"]);
        workouts.forEach((w) => w.category && s.add(w.category));
        return [...s];
    }, [workouts]);

    const filtered = useMemo(
        () => (cat === "전체" ? workouts : workouts.filter((w) => w.category === cat)),
        [workouts, cat]
    );

    const [editMode, setEditMode] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorMode, setEditorMode] = useState("create");
    const [editing, setEditing] = useState(null);

    const openCreate = () => {
        setEditing(null);
        setEditorMode("create");
        setEditorOpen(true);
    };

    const openEdit = (w) => {
        const initial = {
            ...w,
            category: w.category === "웨이트" ? "웨이트" : "유산소운동",
            ...(w.sets_count && !w.sets
                ? {
                    sets: Array.from({ length: Number(w.sets_count) }).map((_, i) => ({
                        set_no: i + 1,
                        weight_kg: w.weight_kg ?? undefined,
                        reps: w.reps ?? undefined,
                    })),
                }
                : null),
        };
        setEditing(initial);
        setEditorMode("edit");
        setEditorOpen(true);
    };

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const requestDelete = (w) => {
        setDeleteTarget({ ...w, date: dateStr });
        setDeleteOpen(true);
    };

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard className="flex-1 w-full mx-auto" title={"머신 세팅"}>
                    <div className="flex flex-col flex-1 pt-3 pb-24">
                        <div className="mb-3" ref={catRef}>
                            <button
                                type="button"
                                onClick={() => setIsCatOpen((v) => !v)}
                                aria-haspopup="listbox"
                                aria-expanded={isCatOpen}
                                className="w-full h-10 px-4 rounded-lg border border-gray-200 bg-gray-100 text-gray-800
                           flex items-center justify-between shadow-sm active:scale-[0.99] transition"
                            >
                                <span className="font-medium truncate">{cat}</span>
                                <ChevronDownIcon
                                    className={`w-5 h-5 text-gray-700 transition-transform ${isCatOpen ? "rotate-180" : ""}`}
                                />
                            </button>

                            {isCatOpen && (
                                <div role="listbox" className="relative">
                                    <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-md">
                                        {categories.map((c) => (
                                            <li key={c} role="option" aria-selected={cat === c}>
                                                <button
                                                    onClick={() => {
                                                        setCat(c);
                                                        setIsCatOpen(false);
                                                    }}
                                                    className={`w-full h-10 px-4 text-left transition
                           ${cat === c ? "bg-[#E8F1FF] text-[#1F4FD3] font-semibold" : "hover:bg-gray-100 text-gray-800"}`}
                                                >
                                                    {c}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <section className="mt-2 space-y-4">
                            {isLoading ? (
                                <div className="text-center text-gray-500 py-12">불러오는 중…</div>
                            ) : filtered.length === 0 ? (
                                <div className="text-center text-gray-500 py-12">아직 기록이 없어요</div>
                            ) : (
                                filtered.map((w) => (
                                    <article
                                        key={w.id}
                                        className={`relative rounded-xl border border-gray-200 p-4 transition
                    ${editMode ? "bg-white" : "hover:bg-gray-50 cursor-pointer"}`}
                                        onClick={!editMode ? () => openEdit(w) : undefined}
                                    >
                                        <div className="grid grid-cols-[30%_65%] gap-4 items-center">
                                            <div className="rounded-full flex items-center justify-center">
                                                {(() => {
                                                    const meta = pickMeta(w);
                                                    return (
                                                        <img
                                                            src={meta.icon}
                                                            alt={`${normalizeTitle(w.title)} 아이콘`}
                                                            className="w-20 h-20 md:w-24 md:h-24 object-contain"
                                                        />
                                                    );
                                                })()}
                                            </div>

                                            <div className="space-y-1">
                                                <div className="flex items-center justify-between gap-2">
                                                    <h4 className="font-semibold text-lg md:text-xl truncate py-1">
                                                        {w.title || "운동"}
                                                    </h4>

                                                    {editMode && (
                                                        <div className="flex items-center shrink-0">
                                                            <button
                                                                aria-label="편집"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openEdit(w);
                                                                }}
                                                                className="px-1 h-8 rounded-md hover:bg-gray-100 active:scale-95"
                                                                title="편집"
                                                            >
                                                                <PencilSquareIcon className="w-5 h-5 text-gray-700" />
                                                            </button>
                                                            <button
                                                                aria-label="삭제"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    requestDelete(w);
                                                                }}
                                                                className="px-1 h-8 rounded-md hover:bg-gray-100 active:scale-95"
                                                                title="삭제"
                                                                disabled={isDeleting}
                                                            >
                                                                <TrashIcon className="w-5 h-5 text-gray-700" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                <hr className="border-gray-200" />

                                                {(() => {
                                                    const metrics = metricLines(w);
                                                    return (
                                                        <dl className="grid grid-cols-2 gap-y-2 pt-1 text-sm md:text-base ">
                                                            {metrics.length > 0
                                                                ? metrics.flatMap(([k, v], i) => [
                                                                    <dt key={`k-${i}`} className="text-gray-500">
                                                                        {k}
                                                                    </dt>,
                                                                    <dd key={`v-${i}`} className="text-left">
                                                                        {v}
                                                                    </dd>,
                                                                ])
                                                                : (
                                                                    <dd className="col-span-2 text-gray-500">
                                                                        상세 정보가 없습니다
                                                                    </dd>
                                                                )}
                                                        </dl>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </article>
                                ))
                            )}
                        </section>
                    </div>
                </AuthCard>
            )}

            {editorOpen && (
                <MachineSettingModal
                    open={editorOpen}
                    mode={editorMode}
                    date={format(dateObj, "yyyy-MM-dd")}
                    initial={editing}
                    onClose={() => setEditorOpen(false)}
                    context="settings"
                    onSubmit={(payload) => {
                        setWorkouts((prev) =>
                            prev.map((w) =>
                                w.title === payload.title
                                    ? {
                                        ...w,
                                        sets: payload.sets,
                                        weight_kg: payload.sets?.[0]?.weight_kg,
                                        reps: payload.sets?.[0]?.reps,
                                        sets_count: payload.sets?.length,
                                        duration_min: payload.duration_min,
                                        speed_kph: payload.cardio?.speed_kph,
                                        distance_km: payload.cardio?.distance_km,
                                        watt_w: payload.cardio?.watt_w ?? payload.cardio?.watt,
                                        target_hr_bpm:
                                            payload.target_hr_bpm ??
                                            payload.cardio?.target_hr_bpm ??
                                            w.target_hr_bpm,
                                    }
                                    : w
                            )
                        );
                    }}
                />
            )}

            <BottomNav className="h-16" />
        </main>
    );
}
